import { useState } from "react";
import { T } from "@/components/T";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { AuthShell, PasswordField } from "@/pages/Login";
import { resetPassword } from "@/lib/api";

function formatError(detail) {
  if (!detail) return "Something went wrong.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 8) { setError("Password must be at least 8 characters."); return; }
    if (password !== confirm) { setError("Passwords do not match."); return; }
    setBusy(true);
    try {
      await resetPassword(token, password);
      setDone(true);
      setTimeout(() => navigate("/login"), 2500);
    } catch (err) {
      setError(formatError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  if (!token) {
    return (
      <AuthShell title="Reset password" subtitle="Invalid link">
        <div className="space-y-4 text-center" data-testid="reset-no-token">
          <p className="text-sm text-ink-2"><T>This reset link is missing its token. Please request a new link.</T></p>
          <Link to="/forgot-password" className="inline-block text-sm text-saffron link-underline"><T>Request a new link</T></Link>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell title="Set a new password" subtitle="Choose a strong password you'll remember">
      {done ? (
        <div className="space-y-4 text-center" data-testid="reset-done">
          <p className="text-4xl text-saffron" aria-hidden="true">ॐ</p>
          <p className="text-sm text-ink-2"><T>Your password has been reset. Redirecting you to sign in…</T></p>
          <Link to="/login" className="inline-block text-sm text-saffron link-underline"><T>Sign in now</T></Link>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="mb-1.5 block text-sm text-ink-2"><T>New password</T></label>
            <PasswordField testId="reset-password" required value={password} onChange={(e) => setPassword(e.target.value)} />
          </div>
          <div>
            <label className="mb-1.5 block text-sm text-ink-2"><T>Confirm new password</T></label>
            <PasswordField testId="reset-confirm" required value={confirm} onChange={(e) => setConfirm(e.target.value)} />
          </div>
          {error && <p data-testid="reset-error" className="text-sm text-[var(--saffron-dark)]">{error}</p>}
          <button data-testid="reset-submit" disabled={busy} className="w-full bg-[var(--saffron)] py-3 font-medium text-white transition-colors hover:bg-[var(--saffron-dark)] disabled:opacity-60">
            {busy ? "Saving…" : "Reset password"}
          </button>
        </form>
      )}
    </AuthShell>
  );
}
